
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Trash2, ArrowLeft, ScrollText } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { getRpgThemeClasses } from "@/lib/utils";
import { JournalEntry } from "@/hooks/journal/types";
import ExportButton from "@/components/export/ExportButton";
import { useIsMobile } from "@/hooks/use-mobile";

interface JournalEntryHeaderProps {
  entryId?: string;
  journalEntry?: JournalEntry;
  onDeleteEntry: () => Promise<void>;
}

const JournalEntryHeader: React.FC<JournalEntryHeaderProps> = ({
  entryId,
  journalEntry,
  onDeleteEntry
}) => {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const { darkMode, enableRpgMode } = useSettings();
  const rpgTheme = getRpgThemeClasses(enableRpgMode, darkMode);
  const isMobile = useIsMobile();

  const handleBackClick = () => {
    navigate("/history");
  };

  const handleDeleteClick = async () => {
    try {
      setIsDeleting(true);
      await onDeleteEntry();
    } finally {
      setIsDeleting(false);
    }
  };

  const formatEntryId = (id: string) => {
    if (!enableRpgMode) return null;
    const shortId = id.split('-')[0];
    return `Quest #${shortId}`;
  };

  return (
    <div className="mb-4 space-y-2 sm:space-y-0">
      {/* Top row with back button and entry ID */}
      <div className="flex items-center justify-between">
        <Button 
          variant={enableRpgMode ? "outline" : "outline"}
          size={isMobile ? "sm" : "sm"}
          onClick={handleBackClick} 
          className={cn(
            "flex items-center min-h-[44px]",
            enableRpgMode && rpgTheme.button + " flex gap-1"
          )}
        >
          <ArrowLeft className="h-4 w-4" />
          {!isMobile && (enableRpgMode ? "Return to Chronicle" : "Back to History")}
        </Button>
        
        {/* Entry ID - show on mobile if RPG mode */}
        {entryId && enableRpgMode && (
          <div className={cn(
            "flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md",
            "bg-amber-900/40 border border-amber-600/50 text-amber-200",
            "font-medium text-xs tracking-wide",
            isMobile && "text-xs px-2"
          )}>
            <ScrollText className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            <span className={cn(isMobile && "hidden xs:inline")}>
              {formatEntryId(entryId)}
            </span>
          </div>
        )}
      </div>
      
      {/* Bottom row with action buttons */}
      <div className="flex justify-end gap-2">
        {journalEntry && (
          <ExportButton
            entries={[journalEntry]}
            variant="outline"
            size={isMobile ? "sm" : "sm"}
            showText={!isMobile}
            className={cn(
              "min-h-[44px]",
              enableRpgMode && rpgTheme.button
            )}
          />
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="destructive" 
              size={isMobile ? "sm" : "sm"}
              className={cn(
                "flex items-center min-h-[44px]",
                enableRpgMode && "bg-red-900 hover:bg-red-800 text-red-100 border border-red-700/50"
              )}
            >
              <Trash2 className="h-4 w-4" />
              {!isMobile && (
                <span className="ml-1">
                  {enableRpgMode ? "Destroy Quest" : "Delete"}
                </span>
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className={cn(
            "mx-4 max-w-sm sm:max-w-lg",
            enableRpgMode && rpgTheme.cardBackground
          )}>
            <AlertDialogHeader>
              <AlertDialogTitle className={cn(
                enableRpgMode && rpgTheme.text
              )}>
                {enableRpgMode ? "Destroy this Quest?" : "Are you sure?"}
              </AlertDialogTitle>
              <AlertDialogDescription className={cn(
                enableRpgMode && rpgTheme.accent
              )}>
                {enableRpgMode 
                  ? "This will permanently remove this quest from your chronicle. This action cannot be undone."
                  : "This will permanently delete this journal entry. This action cannot be undone."
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <AlertDialogCancel className={cn(
                "w-full sm:w-auto",
                enableRpgMode && "bg-amber-900/30 border-amber-700 text-amber-200 hover:bg-amber-900/50"
              )}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDeleteClick} 
                disabled={isDeleting}
                className={cn(
                  "bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full sm:w-auto",
                  enableRpgMode && "bg-red-900 hover:bg-red-800 text-red-100 border border-red-700/50"
                )}
              >
                {isDeleting 
                  ? (enableRpgMode ? "Destroying..." : "Deleting...") 
                  : (enableRpgMode ? "Destroy" : "Delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default JournalEntryHeader;
